import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { MainRoute } from 'src/navigations/Routes/MainRoute';
import { hasFeature } from 'src/utils/userUtils';
import {
  ActionOutboxEntityType,
  Feature,
  Permission,
} from 'src/WebApiController';

import { useUserHasAnyOfPermissions } from './useUserHasAnyOfPermissions';

export const useUserHasFeature = (feature: Feature | null | undefined) => {
  const { appContext, loginContext } = useAppContext();

  const hasFeatureResult = useMemo(
    () => hasFeature(loginContext?.user, appContext?.features, feature),
    [appContext?.features, feature, loginContext?.user]
  );

  return hasFeatureResult;
};

export const useUserHasGrpcCache = () => {
  const { loginContext } = useAppContext();

  return loginContext?.user?.activeAccount?.isCached;
};

export const useUserHasTableTotalsFeature = (
  entityType?: ActionOutboxEntityType
) => {
  const { mainRoute } = useAppContext();

  // -- Permission mapping --
  const entityTypeToPermission: Partial<
    Record<ActionOutboxEntityType, Permission>
  > = {
    [ActionOutboxEntityType.Listing]: Permission.Inventory_ViewColumnTotals,
    [ActionOutboxEntityType.Sale]: Permission.Sales_ViewColumnTotals,
    [ActionOutboxEntityType.Purchase]: Permission.Purchases_ViewColumnTotals,
  };

  const routeToPermission: Partial<Record<MainRoute, Permission>> = {
    [MainRoute.Inventory]: Permission.Inventory_ViewColumnTotals,
    [MainRoute.InventoryEvent]: Permission.Inventory_ViewColumnTotals,
    [MainRoute.InventorySearch]: Permission.Inventory_ViewColumnTotals,
    [MainRoute.InventorySearchV2]: Permission.Inventory_ViewColumnTotals,
    [MainRoute.Sales]: Permission.Sales_ViewColumnTotals,
    [MainRoute.SalesEvent]: Permission.Sales_ViewColumnTotals,
    [MainRoute.SalesSearch]: Permission.Sales_ViewColumnTotals,
    [MainRoute.SalesSearchV2]: Permission.Sales_ViewColumnTotals,
    [MainRoute.Purchases]: Permission.Purchases_ViewColumnTotals,
    [MainRoute.PurchasesEvent]: Permission.Purchases_ViewColumnTotals,
    [MainRoute.PurchaseSearch]: Permission.Purchases_ViewColumnTotals,
    [MainRoute.PurchaseSearchV2]: Permission.Purchases_ViewColumnTotals,
  };

  const entityTypePermission =
    entityType != null && entityTypeToPermission[entityType];

  const permissionToCheck =
    entityTypePermission || routeToPermission[mainRoute] || null;

  return useUserHasAnyOfPermissions(
    ...(permissionToCheck ? [permissionToCheck] : [])
  );
};

export const useRefetchCatalogAndEntityOnNavigateFeature = (
  entityType?: ActionOutboxEntityType
) => {
  const { mainRoute } = useAppContext();

  const hasAllFeature = useUserHasFeature(
    Feature.RefetchCatalogAndEntityOnNavigate_All
  );

  const hasEntitySpecificFeature = useUserHasFeature(
    // Use entityType to override when explicitly provided - it affects the display of side tables (for example, Inventory table in Sales Event)
    entityType === ActionOutboxEntityType.Listing
      ? Feature.RefetchCatalogAndEntityOnNavigate_Inventory
      : entityType === ActionOutboxEntityType.Sale
        ? Feature.RefetchCatalogAndEntityOnNavigate_Sales
        : entityType === ActionOutboxEntityType.Purchase
          ? Feature.RefetchCatalogAndEntityOnNavigate_Purchase
          : [MainRoute.Inventory, MainRoute.InventoryEvent].includes(mainRoute)
            ? Feature.RefetchCatalogAndEntityOnNavigate_Inventory
            : [MainRoute.Sales, MainRoute.SalesEvent].includes(mainRoute)
              ? Feature.RefetchCatalogAndEntityOnNavigate_Sales
              : [MainRoute.Purchases, MainRoute.PurchasesEvent].includes(
                    mainRoute
                  )
                ? Feature.RefetchCatalogAndEntityOnNavigate_Purchase
                : Feature.RefetchCatalogAndEntityOnNavigate_All
  );

  return hasAllFeature || hasEntitySpecificFeature;
};

export const useUserHasAdvancedSearchV2Feature = (
  entityType?: ActionOutboxEntityType
) => {
  const { mainRoute } = useAppContext();

  const hasAllFeature = useUserHasFeature(Feature.AdvancedSearchV2);

  const hasSpecificFeature = useUserHasFeature(
    entityType === ActionOutboxEntityType.Listing
      ? Feature.AdvancedSearchV2_Inventory
      : entityType === ActionOutboxEntityType.Sale
        ? Feature.AdvancedSearchV2_Sales
        : entityType === ActionOutboxEntityType.Purchase
          ? Feature.AdvancedSearchV2_Purchase
          : [
                MainRoute.Inventory,
                MainRoute.InventoryEvent,
                MainRoute.InventorySearch,
                MainRoute.InventorySearchV2,
              ].includes(mainRoute)
            ? Feature.AdvancedSearchV2_Inventory
            : [
                  MainRoute.Sales,
                  MainRoute.SalesEvent,
                  MainRoute.SalesSearch,
                  MainRoute.SalesSearchV2,
                ].includes(mainRoute)
              ? Feature.AdvancedSearchV2_Sales
              : [
                    MainRoute.Purchases,
                    MainRoute.PurchasesEvent,
                    MainRoute.PurchaseSearch,
                    MainRoute.PurchaseSearchV2,
                  ].includes(mainRoute)
                ? Feature.AdvancedSearchV2_Purchase
                : null
  );

  return hasAllFeature || hasSpecificFeature;
};
